// imports
import { Box, CircularProgress } from '@mui/material';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { useCallback, useContext, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

// components
import { Alert } from 'components/common/Alert';
import { AuthLayout } from 'components/common/AuthLayout';
import { PlainLayout } from 'components/common/PlainLayout';
import CheckoutForm from 'components/main/CheckoutForm';

// interfaces, helpers, constants
import {
  LET_S_FINISH_POWERING_YOU_UP_TEXT,
  PRODUCT_LOADING_TEXT,
  SEVEN_DAY_FREE_TRIAL_TEXT,
} from 'constants/constant';
import { DASHBOARD_ROUTE, ENV, HTTP_STATUS } from 'constants/index';
import { useAuthContext } from 'contexts/AuthContext';
import { UserSubscriptionsContext } from 'contexts/UserSubscriptions';
import { useFindOnePriceAndProductsLazyQuery } from 'generated/graphql';
import { ModeType } from 'interfaces';
import { isAdmin } from 'lib/helper';

const Checkout = () => {
  const stripePromise = loadStripe(ENV.STRIPE_KEY);
  const navigate = useNavigate();

  const { priceId } = useParams();
  const [hasFetched, setHasFetched] = useState(false);
  const { userSubscription } = useContext(UserSubscriptionsContext);
  const { id } = userSubscription || {};

  const { userRoles } = useAuthContext();
  const admin = isAdmin(userRoles);

  const [findPriceAndProduct, { data, loading }] = useFindOnePriceAndProductsLazyQuery({
    onCompleted: (data) => {
      const { findOnePriceAndProducts } = data || {};
      const { response } = findOnePriceAndProducts || {};
      const { status } = response || {};
      if (status === HTTP_STATUS.SUCCESS) {
        setHasFetched(true);
      } else {
        Alert.error(response?.message ?? '');
      }
    },
    onError: ({ message }) => {
      Alert.error(message ?? '');
    },
  });

  const { price } = data?.findOnePriceAndProducts || {};
  const { amount, interval, isTrialEnded, billAbleAmount } = price || {};

  const fetchPriceAndProduct = useCallback(() => {
    findPriceAndProduct({
      variables: {
        getStripePriceInput: {
          id: priceId as string,
        },
      },
    });
  }, [findPriceAndProduct, priceId]);

  useEffect(() => {
    id && navigate(DASHBOARD_ROUTE);
  }, [id, navigate]);

  useEffect(() => {
    if (!hasFetched && priceId && admin) {
      fetchPriceAndProduct();
    }
  }, [fetchPriceAndProduct, hasFetched, priceId, admin]);

  if (loading || !hasFetched) {
    return (
      <AuthLayout title={PRODUCT_LOADING_TEXT}>
        <Box display="flex" flexDirection="column" alignItems="center">
          <CircularProgress size={40} thickness={4} color="primary" />
        </Box>
      </AuthLayout>
    );
  }

  return (
    <PlainLayout
      title={
        <>
          {LET_S_FINISH_POWERING_YOU_UP_TEXT} <br />
          {SEVEN_DAY_FREE_TRIAL_TEXT}
        </>
      }
      maxWidth="1200px">
      <Elements stripe={stripePromise}>
        <CheckoutForm
          priceId={priceId || ''}
          amount={amount}
          interval={interval}
          isTrialEnded={isTrialEnded}
          mode={ModeType.CREATE_ADMIN}
          billAbleAmount={billAbleAmount}
        />
      </Elements>
    </PlainLayout>
  );
};

export default Checkout;
